import { useDispatch, useSelector } from "react-redux";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ConfigProvider, theme as antdTheme } from "antd";
import ptBR from "antd/locale/pt_BR";
import { routes } from "./routes";
import AuthRoute from "./components/Services/AuthRoute";
import LoadingScreen from "./components/LoadingScreen";
import { useEffect, useLayoutEffect, useMemo } from "react";
import { initializeApp, syncReportUser } from "./auth/actions/userActions";
import { READY_APP } from "./hooks/user";
import { END_GET_USER_REPORT_DATA } from "./hooks/report";
import { getUser, useTokenExpirationRedirect } from "./utils/user-manager";

// Hooks de navegação (useNavigate) só funcionam dentro do Router — este
// subcomponente vive dentro do BrowserRouter, ao contrário do App.
const AppRoutes = () => {
    useTokenExpirationRedirect();
    return (
        <Routes>
            {routes.map((route, index) => (
                <Route key={index} path={route.path} element={<AuthRoute {...route} />} />
            ))}
        </Routes>
    );
};

const App = () => {
    const { checked, authenticated, user } = useSelector((state) => state.session);
    const { ready } = useSelector((state) => state.user);
    const { isDarkMode } = useSelector((state) => state.theme);
    const dispatch = useDispatch();

    // Espelha o tema nos CSS vars (tokens.css) — skin.scss e estilos inline
    // com var(--cp-*) acompanham o antd ao alternar. useLayoutEffect (e não
    // useEffect): precisa rodar antes do paint para que CSS vars e cssinjs do
    // antd mudem no mesmo frame — com useEffect o browser pode pintar um frame
    // com metade da tela em cada tema. A classe theme-switching zera as
    // transitions (skin.scss) durante a troca, senão os elementos com
    // transition ficam ~300ms num estado híbrido.
    useLayoutEffect(() => {
        const root = document.documentElement;
        root.classList.add("theme-switching");
        root.setAttribute("data-theme", isDarkMode ? "dark" : "light");
        // dois rAFs: garante que o paint já aplicou o tema novo antes de
        // reabilitar as transitions
        const id = requestAnimationFrame(() => {
            requestAnimationFrame(() => root.classList.remove("theme-switching"));
        });
        return () => {
            cancelAnimationFrame(id);
            root.classList.remove("theme-switching");
        };
    }, [isDarkMode]);
    useEffect(() => {
        const initialize = async () => {
            if (checked && !ready) {
                if (authenticated && user?.sub) {
                    const userData = await getUser();
                    if (userData && !userData.expired) {
                        dispatch(initializeApp(user.sub));

                        // Automatic Metabase Sync if missing
                        if (!localStorage.getItem("user-report")) {
                            syncReportUser(user.sub).then(responseReport => {
                                const { data: dataReport, status: statusReport } = responseReport;
                                if (statusReport === 201) {
                                    dispatch({ type: END_GET_USER_REPORT_DATA, payload: dataReport.user });
                                    localStorage.setItem("user-report", JSON.stringify(dataReport.user));
                                }
                            }).catch(e => {
                                console.log("Falha ao sincronizar Metabase auto:", e.message);
                            });
                        }
                    } else {
                        dispatch({ type: READY_APP });
                    }
                } else if (!authenticated) {
                    dispatch({ type: READY_APP });
                }
            }
        };

        initialize();
    }, [checked, authenticated, ready, dispatch, user]);

    // Memoizado por isDarkMode: um objeto novo a cada render invalidaria o
    // contexto de tema do antd e re-renderizaria a árvore inteira à toa.
    const themeConfig = useMemo(
        () => ({
            algorithm: isDarkMode ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
            token: {
                // Espelha styles/tokens.css (fonte única das cores da marca)
                colorPrimary: "#0e9c84",
                colorSuccess: "#0e9c84",
                colorWarning: "#e8a33d",
                colorError: "#e5533c",
                colorInfo: "#2d7ff9",
                colorLink: "#0e9c84",
                colorBgLayout: isDarkMode ? "#101820" : "#f4f6f8",
                // Alinha superfícies/bordas do antd aos CSS vars escuros
                // (tokens.css) para não haver dois tons de "dark" na tela.
                ...(isDarkMode && {
                    colorBgContainer: "#16212c",
                    colorBgElevated: "#1b2733",
                    colorBorder: "#3a4956",
                    colorBorderSecondary: "#2a3743",
                }),
                borderRadius: 8,
                fontFamily:
                    '"Inter Variable", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                fontSize: 13,
            },
            components: {
                Button: {
                    // Respiro entre ícone e texto (default do antd: 8)
                    iconGap: 12,
                },
            },
        }),
        [isDarkMode]
    );

    // Métodos estáticos (Modal.confirm/info/error, message.*, notification.*)
    // são montados num container próprio fora da árvore React — não enxergam o
    // ConfigProvider abaixo e ficavam sempre no tema claro. holderRender injeta
    // o mesmo tema nesse container global; o antd relê a config a cada chamada,
    // então a troca de tema já vale para o próximo alerta. useLayoutEffect por
    // simetria com o efeito de tema acima: aplica antes do primeiro paint.
    useLayoutEffect(() => {
        ConfigProvider.config({
            holderRender: (children) => (
                <ConfigProvider locale={ptBR} theme={themeConfig}>
                    {children}
                </ConfigProvider>
            ),
        });
    }, [themeConfig]);

    if (!ready) {
        return <LoadingScreen />;
    }

    return (
        <ConfigProvider locale={ptBR} theme={themeConfig}>
            <BrowserRouter>
                <AppRoutes />
            </BrowserRouter>
        </ConfigProvider>
    );
};

export default App;
